import type { ReportQueryResponse } from '@/api/types';

const INTEGRITY_MESSAGE =
  'Perf + Halo data did not satisfy the all-effects attribution contract. Values and export are withheld.';
const FLOW_INTEGRITY_MESSAGE =
  'Perf + Halo production-flow data did not satisfy its Direct/Halo identity contract. Values and export are withheld.';

function metricMicrosAreAdditive(metrics: ReportQueryResponse['totals']): boolean {
  return Number.isSafeInteger(metrics.assigned_conversions_micros)
    && Number.isSafeInteger(metrics.halo_conversions_micros)
    && Number.isSafeInteger(metrics.attributed_conversions_micros)
    && Number.isSafeInteger(metrics.assigned_revenue_micros)
    && Number.isSafeInteger(metrics.halo_revenue_micros)
    && Number.isSafeInteger(metrics.attributed_revenue_micros)
    && metrics.attributed_conversions_micros
      === metrics.assigned_conversions_micros + metrics.halo_conversions_micros
    && metrics.attributed_revenue_micros
      === metrics.assigned_revenue_micros + metrics.halo_revenue_micros;
}

/**
 * Fail closed at the Perf + Halo presentation boundary. Compatibility ticket,
 * cents, and floating-point ROAS aliases are deliberately excluded: only the
 * authoritative micro values must add exactly.
 */
export function perfHaloIntegrityError(
  response: ReportQueryResponse | undefined,
): string | null {
  if (!response) return null;
  if (response.methodology.attribution_scope !== 'all_effects') {
    return INTEGRITY_MESSAGE;
  }
  if (!metricMicrosAreAdditive(response.totals)) return INTEGRITY_MESSAGE;
  if (response.rows.some((row) => !metricMicrosAreAdditive(row.metrics))) {
    return INTEGRITY_MESSAGE;
  }
  return null;
}

/**
 * Halo Analysis additionally requires the production-pair identity to agree
 * with the fact effect. The canonical API classifies equal assigned/purchased
 * production ids as Direct and every off-diagonal pair as Halo; accepting a
 * mixed row here would relabel valid micros even though aggregate additivity
 * still happened to pass.
 */
export function perfHaloFlowIntegrityError(
  response: ReportQueryResponse | undefined,
): string | null {
  if (!response) return null;
  for (const row of response.rows) {
    const assigned = row.dimensions.assigned_production_id;
    const purchased = row.dimensions.sold_production_id;
    if (!assigned || !purchased) return FLOW_INTEGRITY_MESSAGE;
    if (assigned === purchased) {
      if (
        row.metrics.halo_conversions_micros !== 0
        || row.metrics.halo_revenue_micros !== 0
      ) return FLOW_INTEGRITY_MESSAGE;
    } else if (
      row.metrics.assigned_conversions_micros !== 0
      || row.metrics.assigned_revenue_micros !== 0
    ) {
      return FLOW_INTEGRITY_MESSAGE;
    }
  }
  return null;
}
